import { Helmet } from "react-helmet-async";

interface FAQ {
  question: string;
  answer: string;
}

interface PageSchemaProps {
  // Support both prop naming patterns
  pageUrl?: string;
  url?: string;
  pageName?: string;
  title?: string;
  pageDescription?: string;
  description?: string;
  faqs?: FAQ[];
  isServicePage?: boolean;
  serviceName?: string;
  serviceDescription?: string;
}

export const PageSchema = ({
  pageUrl,
  url,
  pageName,
  title,
  pageDescription,
  description,
  faqs = [],
  isServicePage = false,
  serviceName,
  serviceDescription,
}: PageSchemaProps) => {
  // Normalize props to support both naming patterns
  const finalUrl = pageUrl || url || "";
  const finalName = pageName || title || "";
  const finalDescription = pageDescription || description || "";

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "sameAs": []
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${finalUrl}#webpage`,
    "url": finalUrl,
    "name": finalName,
    "description": finalDescription,
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${finalUrl}#faq`,
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const serviceSchema = isServicePage && serviceName ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${finalUrl}#service`,
    "name": serviceName,
    "description": serviceDescription || finalDescription,
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "areaServed": [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "Canada" }
    ],
    "serviceType": "Website Design & Marketing"
  } : null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
    </Helmet>
  );
};
